// Fetch impacted applications list from Azure Function endpoint /fetch_bat_bp
const mode = import.meta.env.MODE;
let baseurl: string;

if (mode === 'production') {
  baseurl = 'https://askawatch.cbre.com';
  console.log('FetchIncidents: Running in PRODUCTION mode');
} else if (mode === 'staging') {
  baseurl = 'https://askawatch-test.cbre.com';
  console.log('FetchIncidents: Running in non prod mode');
} else {
  baseurl = 'http://localhost:8000';
  console.log('FetchIncidents: Running in DEVELOPMENT mode');
}

const FETCH_APPS_URL =
  (import.meta as any).env.VITE_FETCH_INCIDENTS_URL ||
  `${baseurl}/api/bat/fetch_applications`;

const FETCH_APP_EMAIL_BASE =
  (import.meta as any).env.VITE_FETCH_APP_EMAIL_URL ||
  `${baseurl}/api/bat/fetch_bat_communication_dl`; // expects ?number= query

export interface ApplicationItem {
  number: string | number;
  name: string;
}

interface ApplicationsResponse {
  success?: boolean;
  applications?: Array<ApplicationItem>;
  count?: number;
}

function normalizeApplications(data: ApplicationsResponse | any): ApplicationItem[] {
  const list: any[] = data?.applications || [];
  return list
    .filter(
      (v) =>
        v &&
        (typeof v.name === 'string') &&
        (v.number !== undefined && v.number !== null)
    )
    .map((v) => ({
      name: String(v.name).trim(),
      number: typeof v.number === 'number' || /^\d+$/.test(String(v.number))
        ? v.number
        : String(v.number).trim()
    }))
    .filter((v) => v.name)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Fetch full application objects (number + name)
 */
export async function fetchBatApplications(): Promise<ApplicationItem[]> {
  const r = await fetch(FETCH_APPS_URL, { method: 'GET' });
  if (!r.ok) throw new Error(`Fetch applications failed: ${r.status}`);
  const data: ApplicationsResponse = await r.json();
  const apps = normalizeApplications(data);

  if (data.success === false) {
    throw new Error('Fetch applications returned success=false');
  }
  if (!apps.length) {
    console.warn('fetchBatApplications: empty applications list');
  }
  return apps;
}

/**
 * Fetch multiple numbers at once; returns map number -> distribution_list (if found).
 */
export async function fetchApplicationDlsMulti(appNumbers: Array<string | number>): Promise<Record<string, string>> {
  const nums = appNumbers.filter(n => n != null).map(n => String(n));
  if (!nums.length) return {};
  const params = nums.map(n => `numbers=${encodeURIComponent(n)}`).join('&');
  const url = `${FETCH_APP_EMAIL_BASE}?${params}`;
  const r = await fetch(url, { method: 'GET' });
  if (!r.ok) {
    console.warn('fetchApplicationDlsMulti: non-OK', r.status);
    return {};
  }
  const data = await r.json();
  if (data.success === false) return {};

  const entries: Array<{ number: string | number; distribution_list?: string | null }> = data.entries || [];
  const result: Record<string, string> = {};
  for (const e of entries) {
    if (e.distribution_list) {
      const cleaned = String(e.distribution_list).trim();
      if (cleaned) result[String(e.number)] = cleaned;
    }
  }
  console.log('fetchApplicationDlsMulti: fetched DLs for', result, 'applications');
  return result;
}